import { useEffect, useState } from 'react'

import { api } from '../api'
import { PresetNumber, Stepper } from '../components/NumberPick'
import { t } from '../i18n'
import { haptic, notify } from '../telegram'
import { americanoPlan, kingPlan, mexicanoPlan, pairsPlan } from '../tournamentPlan'
import { isIndividualKind, isTournamentKind } from '../types'
import type { Club, GameKind, PlayerMe, ScoreMode } from '../types'

interface Props {
  me: PlayerMe
  /** Тренер пришёл из профиля кнопкой «Групповая тренировка» */
  initialKind?: GameKind
  onCreated: (gameId: number) => void
  onCancel: () => void
}

// Тренировка: уровень учеников задаётся диапазоном, а не от уровня тренера
const TRAINING_LEVELS: [number, number][] = [
  [0, 2],
  [2, 3.5],
  [3.5, 5],
  [0, 7],
]

/** Завтра в локальной дате. toISOString() давал бы дату по UTC —
 *  после полуночи по Мадриду форма предлагала бы «сегодня». */
function defaultDate(): string {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

/** Клубы по городам: свой город первым, остальные — по алфавиту. */
function groupByCity(clubs: Club[], home: string): [string, Club[]][] {
  const groups = new Map<string, Club[]>()
  for (const club of clubs) {
    groups.set(club.city, [...(groups.get(club.city) ?? []), club])
  }
  return [...groups.entries()].sort(([a], [b]) =>
    a === home ? -1 : b === home ? 1 : a.localeCompare(b),
  )
}

export default function CreateGame({ me, initialKind = 'game', onCreated, onCancel }: Props) {
  const [clubs, setClubs] = useState<Club[]>([])
  const [clubId, setClubId] = useState<number | null>(null)
  const [date, setDate] = useState(defaultDate())
  const [startTime, setStartTime] = useState('20:00')
  const [duration, setDuration] = useState(90)
  const [spread, setSpread] = useState(0.5)
  const [price, setPrice] = useState<string>('')
  const [maxPlayers, setMaxPlayers] = useState(4)
  const [booked, setBooked] = useState(false)
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  // Турнир: формат, размер, длина матча
  const [kind, setKind] = useState<GameKind>(initialKind)
  const [levelPreset, setLevelPreset] = useState(1)
  const [size, setSize] = useState(8)
  const [points, setPoints] = useState(24)
  const [scoreMode, setScoreMode] = useState<ScoreMode>('points')
  const [rounds, setRounds] = useState(0)
  const [courts, setCourts] = useState(0)
  const [rated, setRated] = useState(true)
  const [mixed, setMixed] = useState(false)
  // «Король корта»: минут на раунд (таймер)
  const [roundMinutes, setRoundMinutes] = useState(12)
  const [myGender, setMyGender] = useState<'m' | 'f' | null>(me.gender)
  const isTournament = isTournamentKind(kind)
  // Турниры — только с разрешения администратора (или у одобренного тренера)
  const [canOrganize, setCanOrganize] = useState(me.can_organize)
  const [orgStatus, setOrgStatus] = useState(me.organizer_status)
  // Разрешение могли выдать, пока приложение было открыто — уточняем при входе в форму
  useEffect(() => {
    if (me.can_organize) return
    api.me().then(
      (fresh) => {
        setCanOrganize(fresh.can_organize)
        setOrgStatus(fresh.organizer_status)
      },
      () => undefined,
    )
  }, [me.can_organize])
  const [requesting, setRequesting] = useState(false)
  const locked = isTournament && !canOrganize
  const individual = isIndividualKind(kind)
  const pointsMode = individual || scoreMode === 'points'
  const plan =
    kind === 'americano'
      ? americanoPlan(size, rounds, courts)
      : kind === 'mexicano'
        ? mexicanoPlan(size, rounds, courts)
        : kind === 'pairs'
          ? pairsPlan(size, courts)
          : kind === 'king'
            ? kingPlan(size, rounds)
            : null
  const king = kind === 'king'
  const isTraining = kind === 'training'
  const kinds: GameKind[] =
    me.coach_status === 'approved'
      ? ['game', 'americano', 'mexicano', 'pairs', 'king', 'training']
      : ['game', 'americano', 'mexicano', 'pairs', 'king']

  function pickKind(next: GameKind) {
    setKind(next)
    // Разумные значения по умолчанию для каждого формата
    setRounds(0)
    setCourts(0)
    if (next === 'americano' || next === 'mexicano') {
      setSize(8)
      setPoints(24)
      setScoreMode('points')
      setDuration(120)
    } else if (next === 'pairs') {
      setSize(8)
      setPoints(6)
      setScoreMode('games')
      setDuration(180)
    } else if (next === 'king') {
      // Раунды по времени, по умолчанию без лимита: играют, пока есть корт
      setSize(8)
      setScoreMode('games')
      setRounds(-1)
      setRoundMinutes(12)
      setDuration(120)
    } else {
      setDuration(90)
      if (next === 'training') setMaxPlayers(4)
    }
  }

  // Все клубы побережья: игроки ездят между городами, а капитан из Кальпе
  // вполне может собирать игру в Морайре. По умолчанию — первый клуб своего города.
  useEffect(() => {
    void api.clubs().then((list) => {
      setClubs(list)
      const home = list.find((c) => c.city === me.city) ?? list[0]
      if (home) setClubId(home.id)
    })
  }, [me.city])

  const base = me.level ?? 3
  const levelMin = isTraining ? TRAINING_LEVELS[levelPreset][0] : Math.max(0, base - spread)
  const levelMax = isTraining ? TRAINING_LEVELS[levelPreset][1] : Math.min(7, base + spread)

  async function requestAccess() {
    setRequesting(true)
    haptic('medium')
    try {
      const updated = await api.organizerRequest()
      setOrgStatus(updated.organizer_status)
      notify(t().orgSent)
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setRequesting(false)
    }
  }

  async function submit() {
    if (!clubId) return notify('Выберите клуб')
    const isMixed = kind === 'pairs' && mixed
    if (isMixed && !myGender) return notify(t().genderAskHint)
    setSaving(true)
    haptic('medium')
    try {
      // Организатор микста занимает мужское или женское место — сервер должен это знать
      if (isMixed && !me.gender && myGender) await api.updateMe({ gender: myGender })
      // Собираем локальное время и отдаём в ISO с зоной — сервер хранит в UTC
      const startsAt = new Date(`${date}T${startTime}:00`)
      const created = await api.createGame({
        club_id: clubId,
        starts_at: startsAt.toISOString(),
        duration_min: duration,
        level_min: Number(levelMin.toFixed(2)),
        level_max: Number(levelMax.toFixed(2)),
        price_per_player: price === '' ? null : Number(price),
        max_players: isTournament ? size : maxPlayers,
        court_booked: booked,
        notes: notes || null,
        kind,
        rated: isTraining ? false : rated,
        mixed: kind === 'pairs' && mixed,
        ...(isTournament && !king
          ? {
              points_per_match: points,
              score_mode: individual ? 'points' : scoreMode,
              ...(individual && rounds ? { rounds } : {}),
              ...(courts ? { courts } : {}),
            }
          : {}),
        ...(king ? { score_mode: 'games', round_minutes: roundMinutes, ...(rounds ? { rounds } : {}) } : {}),
      })
      onCreated(created.id)
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Не удалось создать игру')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <div className="row between" style={{ margin: '4px 0 12px' }}>
        <span className="big">{isTraining ? t().createTraining : isTournament ? t().createTournament : t().createGame}</span>
        <button className="ghost" onClick={onCancel}>
          ✕
        </button>
      </div>

      <label>{t().formatLabel}</label>
      <div className="chips">
        {kinds.map((value) => (
          <button key={value} className="chip" aria-pressed={kind === value} onClick={() => pickKind(value)}>
            {isTournamentKind(value) && !canOrganize ? '🔒 ' : ''}
            {value === 'game'
              ? t().kindGame
              : value === 'americano'
                ? t().kindAmericano
                : value === 'mexicano'
                  ? t().kindMexicano
                  : value === 'training'
                  ? t().kindTraining
                  : value === 'king'
                    ? t().kindKing
                    : t().kindPairs}
          </button>
        ))}
      </div>
      <div className="muted small" style={{ marginTop: 6 }}>
        {kind === 'game'
          ? t().kindHintGame
          : kind === 'training'
            ? t().kindHintTraining
            : kind === 'americano'
            ? t().kindHintAmericano
            : kind === 'mexicano'
              ? t().kindHintMexicano
              : kind === 'king'
                ? t().kindHintKing
                : mixed
                  ? t().mixedHint
                  : t().kindHintPairs}
      </div>

      {locked && (
        <div className="card" style={{ marginTop: 12 }}>
          <div className="title">{t().orgLockedTitle}</div>
          <div className="muted" style={{ marginTop: 6 }}>
            {t().orgLockedText}
          </div>
          {orgStatus === 'pending' ? (
            <div className="t-rest small" style={{ marginTop: 10 }}>
              ⏳ {t().orgPending}
            </div>
          ) : (
            <>
              {orgStatus === 'rejected' && (
                <div className="muted small" style={{ marginTop: 8 }}>
                  {t().orgRejected}
                </div>
              )}
              <button style={{ width: '100%', marginTop: 12 }} disabled={requesting} onClick={requestAccess}>
                {t().orgRequest}
              </button>
            </>
          )}
        </div>
      )}

      <div style={locked ? { display: 'none' } : undefined}>

      {kind === 'game' && (
        <>
          <label>{t().gameTypeLabel}</label>
          <div className="chips">
            <button className="chip" aria-pressed={rated} onClick={() => setRated(true)}>
              {t().ratedGame}
            </button>
            <button className="chip" aria-pressed={!rated} onClick={() => setRated(false)}>
              {t().friendlyBadge}
            </button>
          </div>
          <div className="muted small" style={{ marginTop: 6 }}>
            {rated ? t().ratedGameHint : t().friendlyGameHint}
          </div>
        </>
      )}

      {kind === 'pairs' && (
        <>
          <label>
            <span className="row" style={{ gap: 10 }}>
              <input type="checkbox" style={{ width: 20 }} checked={mixed} onChange={(e) => setMixed(e.target.checked)} />
              {t().mixedToggle}
            </span>
          </label>
          {mixed && (
            <>
              {!me.gender && (
                <>
                  <label>{t().genderYours}</label>
                  <div className="chips">
                    <button className="chip" aria-pressed={myGender === 'm'} onClick={() => setMyGender('m')}>
                      {t().genderMan}
                    </button>
                    <button className="chip" aria-pressed={myGender === 'f'} onClick={() => setMyGender('f')}>
                      {t().genderWoman}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}

      {isTournament && (
        <>
          <label>{t().playersCount}</label>
          <Stepper
            value={size}
            min={4}
            max={32}
            step={kind === 'pairs' || king ? 2 : 1}
            label={t().playersCount}
            onChange={setSize}
          />

          {kind === 'pairs' && (
            <>
              <label>{t().scoreModeLabel}</label>
              <div className="seg full">
                {(['games', 'points'] as ScoreMode[]).map((mode) => (
                  <button
                    key={mode}
                    aria-pressed={scoreMode === mode}
                    onClick={() => {
                      setScoreMode(mode)
                      setPoints(mode === 'points' ? 21 : 6)
                    }}
                  >
                    {mode === 'points' ? t().scorePoints : t().scoreGames}
                  </button>
                ))}
              </div>
            </>
          )}

          {king && (
            <>
              <label>{t().roundMinutesLabel}</label>
              <PresetNumber
                value={roundMinutes}
                presets={[10, 12, 15]}
                min={3}
                max={60}
                unit={t().minutes}
                label={t().roundMinutesLabel}
                onChange={setRoundMinutes}
              />
              <div className="muted small" style={{ marginTop: 6 }}>
                {t().kingRoundHint}
              </div>
              <label>{t().roundsLabel}</label>
              <PresetNumber
                value={rounds}
                presets={[5, 7, 9]}
                min={1}
                max={64}
                allowUnlimited
                label={t().roundsLabel}
                onChange={setRounds}
              />
            </>
          )}

          {!king && (
          <>
          <label>{t().matchTo}</label>
          <PresetNumber
            value={points}
            presets={pointsMode ? [16, 21, 24, 32] : [4, 6, 9]}
            min={1}
            max={99}
            unit={pointsMode ? t().pointsUnit : t().gamesUnit}
            label={t().matchTo}
            onChange={setPoints}
          />
          <div className="muted small" style={{ marginTop: 6 }}>
            {(pointsMode ? t().scoreHintPoints : t().scoreHintGames).replace('N', String(points))}{' '}
            {pointsMode ? t().rankHintPoints : t().rankHintGames}
          </div>

          {individual && (
            <>
              <label>{t().roundsLabel}</label>
              <PresetNumber
                value={rounds}
                presets={kind === 'mexicano' ? [4, 6, 8] : [5, 7, 9]}
                min={1}
                max={64}
                allowAuto
                allowUnlimited={kind === 'mexicano'}
                label={t().roundsLabel}
                onChange={setRounds}
              />
            </>
          )}

          <label>{t().courtsLabel}</label>
          <PresetNumber value={courts} presets={[1, 2, 3, 4]} min={1} max={12} allowAuto label={t().courtsLabel} onChange={setCourts} />
          </>
          )}

          {plan && (
            <div className="card muted small" style={{ marginTop: 10 }}>
              {plan.rounds ? t().planLine(size, plan.rounds, plan.courts, plan.perPlayer) : t().planUnlimited(size, plan.courts)}
              {plan.resting > 0 && <div style={{ marginTop: 4 }}>{king ? t().kingRestHint : t().restHint(plan.resting)}</div>}
              {kind === 'americano' && <div style={{ marginTop: 4 }}>{t().roundsHint}</div>}
              {kind === 'mexicano' && <div style={{ marginTop: 4 }}>{t().mexicanoRoundsHint}</div>}
              {king && <div style={{ marginTop: 4 }}>{t().kingLadderHint}</div>}
            </div>
          )}
        </>
      )}

      <label>{t().club}</label>
      <select value={clubId ?? ''} onChange={(e) => setClubId(Number(e.target.value))}>
        {groupByCity(clubs, me.city).map(([city, list]) => (
          <optgroup key={city} label={city}>
            {list.map((club) => (
              <option key={club.id} value={club.id}>
                {club.name}
                {club.indoor ? ' · indoor' : ''}
                {club.price_per_hour != null ? ` · ${club.price_per_hour} €/ч` : ''}
              </option>
            ))}
          </optgroup>
        ))}
      </select>

      <div className="row" style={{ gap: 10 }}>
        <div style={{ flex: 1 }}>
          <label>{t().date}</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <label>{t().time}</label>
          <input
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
          />
        </div>
      </div>

      <label>{t().duration}</label>
      <div className="chips">
        {(isTournament ? [120, 180, 240] : [60, 90, 120]).map((value) => (
          <button
            key={value}
            className="chip"
            aria-pressed={duration === value}
            onClick={() => setDuration(value)}
          >
            {value} {t().minutes}
          </button>
        ))}
      </div>

      <label>
        {isTraining ? t().trainingFor : t().level}: <b>{levelMin.toFixed(2)}–{levelMax.toFixed(2)}</b>
      </label>
      {isTraining && (
        <div className="chips">
          {t().levelPresets.map((label, i) => (
            <button key={label} className="chip" aria-pressed={levelPreset === i} onClick={() => setLevelPreset(i)}>
              {label}
            </button>
          ))}
        </div>
      )}
      <div className="chips" style={isTraining ? { display: 'none' } : undefined}>
        {[0.25, 0.5, 1].map((value) => (
          <button
            key={value}
            className="chip"
            aria-pressed={spread === value}
            onClick={() => setSpread(value)}
          >
            ±{value}
          </button>
        ))}
      </div>

      <div className="row" style={{ gap: 10 }}>
        <div style={{ flex: 1 }}>
          <label>
            {t().price}, € {t().perPlayer}
          </label>
          <input
            type="number"
            inputMode="decimal"
            placeholder="—"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
        </div>
        {kind === 'game' && (
          <div style={{ flex: 1 }}>
            <label>{t().players}</label>
            <input
              type="number"
              min={2}
              max={24}
              value={maxPlayers}
              onChange={(e) => setMaxPlayers(Number(e.target.value))}
            />
          </div>
        )}
      </div>

      {isTraining && (
        <>
          <label>{t().students}</label>
          <div className="chips">
            {[2, 3, 4, 6, 8].map((value) => (
              <button key={value} className="chip" aria-pressed={maxPlayers === value} onClick={() => setMaxPlayers(value)}>
                {value}
              </button>
            ))}
          </div>
        </>
      )}

      <label>
        <span className="row" style={{ gap: 10 }}>
          <input
            type="checkbox"
            style={{ width: 20 }}
            checked={booked}
            onChange={(e) => setBooked(e.target.checked)}
          />
          {t().courtBooked}
        </span>
      </label>

      {isTournament && (
        <label>
          <span className="row" style={{ gap: 10 }}>
            <input type="checkbox" style={{ width: 20 }} checked={rated} onChange={(e) => setRated(e.target.checked)} />
            {t().ratedToggle}
          </span>
        </label>
      )}

      <label>{t().notes}</label>
      <textarea
        rows={2}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Например: играем 2 часа, мячи мои"
      />

      <div style={{ marginTop: 20 }}>
        <button style={{ width: '100%' }} disabled={saving || locked} onClick={submit}>
          {saving ? t().loading : isTraining ? t().createTraining : isTournament ? t().createTournament : t().createGame}
        </button>
      </div>
      </div>
    </div>
  )
}
